/**
 * Every side of an invitation: the sending side (member lists, invite dialogs, in project,
 * dataspace and department settings), `link.*` (what the inviter is told when no mail went out),
 * `inbox.*` and `banner.*` (the recipient's list in Account, and the count the user menu and the
 * home pages show), and `accept.*`, the page the mailed link lands on.
 *
 * `errors.*` is keyed by the server's error codes and looked up dynamically, so
 * those key names are a contract with the API rather than free-form. The accept
 * page's own errors stay out of that table, under `accept.errors.*`, and the inbox's under
 * `inbox.errors.*`.
 */
export default {
	invitations: {
		title: "Members",
		actions: {
			invite: "Invite member",
			send: "Send invitation",
			resend: "Resend invitation",
			cancel: "Cancel invitation",
			dismiss: "Dismiss declined invitation",
		},
		dialogTitle: "Invite a member",
		you: "You",
		fields: {
			email: "Email address",
			emailInvalid: "Enter a valid email address.",
			role: "Role",
		},
		pending: {
			tag: "Pending",
		},
		declined: {
			tag: "Declined",
		},
		invitedBy: "Invited by {name}",
		expiry: {
			in: "Expires in {n} day | Expires in {n} days",
			expired: "Expired",
		},
		confirm: {
			cancel: "Cancel this invitation?",
			remove: "Remove {name} from this project?",
		},
		members: {
			empty: "No members yet.",
		},
		errors: {
			already_member: "This user is already a member.",
			already_invited:
				"An invitation is already pending for this email. Cancel or resend it instead.",
			self_invite: "You cannot invite yourself.",
			not_a_department_member:
				"Only members of this space's department can be invited to it. Invite them to the department first.",
			invitation_mail_disabled:
				"Invitation mail is switched off, or this server cannot send mail, so an invitation cannot be sent again. Cancel it and invite again to get a link to pass on yourself.",
			self_role_change: "You cannot change your own role.",
			email_mismatch: "This invitation was sent to a different email address.",
			expired: "This invitation has expired.",
			generic: "Could not send invitation.",
		},
		status: {
			sent: "Invitation sent.",
			created: "Invitation created. No email was sent.",
			resent: "Invitation resent.",
			cancelled: "Invitation cancelled.",
			dismissed: "Declined invitation dismissed.",
		},
		link: {
			title: "No email was sent",
			body: "Invitation mail is switched off, or this server cannot send mail. Give this link to {email} yourself.",
			label: "Invitation link",
			copy: "Copy link",
			copied: "Link copied.",
			copyFailed: "Could not copy. Select the link and copy it by hand.",
			once: "The link is shown only now and cannot be looked up again. It can be used once, by an account with that email address.",
			done: "Done",
		},
		inbox: {
			title: "Invitations",
			description:
				"What you have been invited to. Invitations are listed by the email address your account provider has verified, so you can answer them here without the link in the email.",
			empty: "You have no pending invitations.",
			emptyNote: "A new one shows up here as soon as it is sent.",
			label: "Pending invitations",
			column: {
				invitation: "Invitation",
				role: "Role",
			},
			kind: {
				project: "Project",
				dataspace: "Dataspace",
				department: "Department",
			},
			meta: "Invited by {name} \u00b7 {expiry}",
			accept: "Accept",
			decline: "Decline",
			acceptFor: "Accept the invitation to {name}",
			declineFor: "Decline the invitation to {name}",
			declineConfirm: {
				title: "Decline the invitation to {name}?",
				note: "{inviter} sees that you declined. The invitation leaves your list, and you can be invited again.",
			},
			accepted: "You joined {name} as {role}.",
			open: "Open {name}",
			declined: "Declined the invitation to {name}.",
			refresh: "Refresh",
			unverified: {
				title: "Your email address isn't verified",
				body: "Your email address isn't verified with your account provider, so invitations sent to {email} cannot be shown or answered here. An invitation binds only to an address the provider has verified. Verify it there, then sign in again.",
				action: "Security settings",
			},
			errors: {
				emailNotVerified:
					"Your email address isn't verified with your account provider, so this invitation can't be answered. Verify it there, then sign in again.",
				gone: "This invitation is no longer valid: it expired, was withdrawn, or was answered elsewhere.",
				notADepartmentMember:
					"This space belongs to a department you are not a member of. Once you have joined the department, you can accept the invitation.",
			},
		},
		banner: {
			title: "You have {n} pending invitation | You have {n} pending invitations",
			action: "View invitations",
		},
		accept: {
			title: "Project invitation",
			titleDataspace: "Dataspace invitation",
			titleDepartment: "Department invitation",
			invitedTo: "You've been invited to {project} as {role}.",
			invitedToEmail: "This invitation was sent to {email}.",
			expires: "This invitation expires {date}.",
			errors: {
				notFound: "Invitation not found.",
				gone: "This invitation is no longer valid.",
				emailMismatch:
					"You're signed in as a different user. Sign out and use the invited email to accept.",
				emailNotVerified:
					"Your email address isn't verified yet. Verify it at the account service, then sign in again.",
			},
			actions: {
				accept: "Accept invitation",
				signInToAccept: "Sign in to accept",
				signOut: "Sign out",
			},
			status: {
				failed: "Could not accept invitation.",
			},
		},
	},
};
